// Cravy Fries site — motion helpers (scroll reveals, parallax, marquee) — verbatim from the design
const {
  useState,
  useEffect,
  useRef
} = React;
function useReveal(opts) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(es => es.forEach(e => {
      if (e.isIntersecting) {
        el.classList.add('on');
        io.disconnect();
      }
    }), {
      threshold: 0.18,
      ...opts
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}
function Reveal({
  as = 'div',
  delay = 0,
  kind = 'up',
  style,
  children,
  ...rest
}) {
  const ref = useReveal();
  const Tag = as;
  return /*#__PURE__*/React.createElement(Tag, {
    ref: ref,
    className: 'rv rv-' + kind,
    style: {
      transitionDelay: delay + 'ms',
      ...style
    },
    ...rest
  }, children);
}
function useParallax(speed = 60, rotate = 0) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const tick = () => {
      const host = el.closest('.cravy-site') || document.getElementById('cravy-site');
      const k = host ? parseFloat(getComputedStyle(host).getPropertyValue('--k')) || 1 : 1;
      const r = el.getBoundingClientRect();
      const p = (r.top + r.height / 2 - innerHeight / 2) / innerHeight;
      el.style.transform = `translateY(${(-p * speed * k).toFixed(1)}px) rotate(${(p * rotate * k).toFixed(2)}deg)`;
      raf = 0;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    addEventListener('scroll', onScroll, {
      passive: true
    });
    tick();
    return () => {
      removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [speed, rotate]);
  return ref;
}
function Floaty({
  speed = 60,
  rotate = 0,
  style,
  children
}) {
  const ref = useParallax(speed, rotate);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    style: {
      willChange: 'transform',
      ...style
    }
  }, children);
}
function Bob({
  dur = 5,
  delay = 0,
  style,
  children
}) {
  return /*#__PURE__*/React.createElement("div", {
    style: {
      animation: `bob ${dur}s ease-in-out ${delay}s infinite`,
      ...style
    }
  }, children);
}
function Marquee({
  text,
  speed = 18,
  bg = '#000',
  color = '#fff',
  border,
  fontSize = 'clamp(22px,2.6vw,40px)',
  angle = 0
}) {
  const items = Array.from({
    length: 10
  }, () => text);
  return /*#__PURE__*/React.createElement("div", {
    style: {
      overflow: 'hidden',
      background: bg,
      color,
      padding: '14px 0',
      transform: angle ? `rotate(${angle}deg) scale(1.02)` : 'none',
      borderTop: border,
      borderBottom: border,
      position: 'relative',
      zIndex: 2
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      gap: 48,
      width: 'max-content',
      animation: `marq ${speed}s linear infinite`,
      fontFamily: 'var(--font-display)',
      fontSize,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      lineHeight: 1
    }
  }, items.concat(items).map((t, i) => /*#__PURE__*/React.createElement("span", {
    key: i,
    style: {
      display: 'flex',
      gap: 48,
      alignItems: 'center'
    }
  }, t, /*#__PURE__*/React.createElement("span", {
    style: {
      fontSize: '0.6em'
    }
  }, "●")))));
}
function useScrollProgress() {
  const ref = useRef(null);
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const el = ref.current;
      if (!el) return raf = 0;
      const r = el.getBoundingClientRect();
      const total = r.height - innerHeight;
      setP(Math.min(1, Math.max(0, -r.top / Math.max(1, total))));
      raf = 0;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => {
      removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return [ref, p];
}
function initLazyScroll(ease = 0.085) {
  if (window.__lazyScroll) return;
  window.__lazyScroll = true;
  let target = scrollY,
    cur = scrollY,
    raf = null;
  const max = () => document.documentElement.scrollHeight - innerHeight;
  addEventListener('wheel', e => {
    if (e.ctrlKey) return;
    e.preventDefault();
    target = Math.max(0, Math.min(max(), target + e.deltaY));
    if (!raf) loop();
  }, {
    passive: false
  });
  addEventListener('scroll', () => {
    if (!raf) {
      target = cur = scrollY;
    }
  }, {
    passive: true
  });
  function loop() {
    raf = requestAnimationFrame(() => {
      cur += (target - cur) * ease;
      if (Math.abs(target - cur) < 0.5) {
        cur = target;
        scrollTo(0, cur);
        raf = null;
        return;
      }
      scrollTo(0, cur);
      loop();
    });
  }
}
Object.assign(window, {
  Reveal,
  Floaty,
  Bob,
  Marquee,
  useScrollProgress,
  useReveal,
  useParallax,
  initLazyScroll
});
